import type { ReactNode, RefObject } from 'react';
import { Popover } from '@base-ui/react/popover';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '@utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { dayLabel } from './calendar.labels';
import styles from './CalendarDayList.module.css';

/** Finds the open list, so the calendar's own Esc leaves it to close first. */
export const DAY_LIST_SELECTOR = '[data-calendar-day-list]:not([data-ending-style])';

interface ICalendarDayListProps {
  day: string;
  /** All of the day's orders. */
  count: number;
  /** How many the cell leaves out. */
  hidden: number;
  /** The day cell: the list opens over it. */
  anchor: RefObject<HTMLDivElement | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The day's chips. */
  children: ReactNode;
}

/** "+N more": the day's whole list in a card over its cell, wider than the cell
 *  so names fit, scrolling past a dozen orders. It opens upward near the
 *  bottom of the month. */
export function CalendarDayList({ day, count, hidden, anchor, open, onOpenChange, children }: ICalendarDayListProps) {
  const { t, i18n } = useTranslation();
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger className={styles.more}>{t('logistics.calendar.more', { n: hidden })}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner
          anchor={anchor}
          side="bottom"
          collisionPadding={8}
          // Over the cell, not under it: the card's top meets the cell's.
          sideOffset={({ anchor: cell }) => -cell.height}
          className={styles.positioner}
        >
          <Popover.Popup className={styles.popup} data-calendar-day-list>
            <header className={styles.header}>
              <Popover.Title className={styles.title}>{dayLabel(day, i18n.language)} <em>{count}</em></Popover.Title>
              <Popover.Close className={styles.close} aria-label={t('common.close')}>
                <CloseIcon size={14} />
              </Popover.Close>
            </header>
            <ScrollArea className={styles.list} viewportClassName={styles.listViewport}>
              {children}
            </ScrollArea>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
